import { createSelector } from '@reduxjs/toolkit';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  PositionId,
  ProgramId,
  allowedTroopPositionIdsForAOLs,
  emptyArr,
  emptyObj,
  positionIdsThatRequirePatrolId,
  youthProfileModuleName,
} from '@shared/constants';
import {
  organizationGuidSel,
  unitTypeIdSel,
} from '@shared/duck/selectors/general.selectors';

import { moduleSel } from './selectorsTyped';

const youthProfileModuleSel = state => state[youthProfileModuleName];

export const memberDetailsSel = state =>
  youthProfileModuleSel(state).memberDetails || emptyObj;

export const leadershipPositionsListSel = state =>
  moduleSel(state).leadershipPositionsList || emptyArr;

export const activeYouthPositionsSel = createSelector(
  leadershipPositionsListSel,
  positions =>
    positions.filter(
      //remove positions that are no longer active
      position => !position.isRetired,
    ),
);

const isAOLmemberSel = createSelector(
  memberDetailsSel,
  organizationGuidSel,
  (memberDetails, organizationGuid) =>
    !!memberDetails?.organizationPositions
      ?.find(orgPos => orgPos.organizationGuid === organizationGuid)
      ?.positions.some(pos => +pos.id === PositionId.ArrowOfLightMember),
);

export const filterPositionsForProgram = createSelector(
  activeYouthPositionsSel,
  unitTypeIdSel,
  isAOLmemberSel,
  (positions, selectedUnitTypeId, isAOLmember) =>
    positions.filter(
      position =>
        !(
          featureFlags.getFlag('SBL_6835_FILTER_VENTURE_PATROL_LEADER') &&
          position.id === PositionId.VenturePatrolLeader
        ) &&
        // AOL members (Packs) need to be able to see some Troop positions, see SBL-5580
        // Currently Packs can not use quick entry to assign positions, if in the future this changes
        // make a custom selector for the quick entry page to deal with this scenario
        ((featureFlags.getFlag('SBL_5580_AOL_POSITIONS') &&
          isAOLmember &&
          allowedTroopPositionIdsForAOLs.includes(position.id)) ||
          // remove positions that don't belong to current unit type
          (position.unitTypes.some(
            unitType => unitType.unitTypeId === Number(selectedUnitTypeId),
          ) &&
            // remove calendar editor for cub scout units
            !(
              position.id === PositionId.CalendarEditor &&
              Number(selectedUnitTypeId) === ProgramId.CUB_SCOUT
            ))),
    ),
);

export const filterPositionsForProgramQESel = createSelector(
  filterPositionsForProgram,
  positions =>
    positions.filter(
      position =>
        // remove Den chief for QE leadershi positions
        position.id !== PositionId.DenChief,
    ),
);

export const filterPositionsRequiringPatrol = patrolId =>
  createSelector(
    filterPositionsForProgram,
    isAOLmemberSel,
    (positions, isAOLmember) =>
      //filter out patrol-related positions if youth is not assigned to a patrol or is not AOL member
      patrolId ||
      (featureFlags.getFlag('SBL_5580_AOL_POSITIONS') && isAOLmember)
        ? positions
        : positions.filter(
            position => !positionIdsThatRequirePatrolId.includes(position.id),
          ),
  );

export const sortedPositionsRequiringPatrol = patrolId =>
  createSelector(filterPositionsRequiringPatrol(patrolId), positions =>
    [...positions].sort((a, b) => a.name.localeCompare(b.name)),
  );

export const positionsOptionsSel = patrolId =>
  createSelector(sortedPositionsRequiringPatrol(patrolId), positions =>
    positions.map(({ id, name }) => ({
      value: id,
      label: name,
    })),
  );

export const isPositionsModalLoadingSel = state =>
  moduleSel(state).isPositionsModalLoading;
